import React from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import { compose, withProps, branch, mapProps } from 'recompose';
import classNames from 'classnames';
import moment from 'moment';

import setTimeplanUnit from '../setTimeplanUnit';
import TimeInput from '../../TimeInput';
import DropdownSelect from '../../../../components/Form/DropdownSelect';
import * as styles from '../timeplan.css';
import deleteTimeplanUnit from '../deleteTimeplanUnit';
import { TimeplanUnitRecord } from '../../../../entities/records';
import { ButtonSmall } from '../../../Button';
import withBooking from '../../../../containers/withBooking';
import withAgreement from '../../../../containers/withAgreement';
import withWorkTypes from '../../../../containers/withWorkTypes';
import PaymentWarning from '../TimeplanUnitPaymentWarning';
import Warnings from './TimeplanUnitWarnings';
import Confirmation from '../../../Confirmation/Confirmation';
import { withTranslation } from 'react-i18next';

function TimeplanUnit({
  disabled,
  from,
  setFrom,
  to,
  setTo,
  hasPayment,
  workType,
  setWorkType,
  workTypes,
  deleteTPU,
  showWarnings,
  overlaps,
  duplicates,
  timeplanUnit,
  absentReason,
  setAbsentReason,
  absentReasons,
  isDeletable,
  overtimeValue,
  setOvertimeMinutes,
  hasOvertimePrices,
}) {
  return (
    <div className={styles.timeplanUnit2}>
      <PaymentWarning hasPayment={hasPayment} />
      <div className={styles.workTypeContainer2}>
        <DropdownSelect
          disabled={disabled}
          className={styles.workTypeSelect}
          input={{
            value: workType,
            onChange: setWorkType,
            onBlur: setWorkType,
          }}
          options={workTypes}
        />
        {!disabled && isDeletable && (
          <ButtonSmall
            icon="trash"
            iconStyle="danger"
            className={styles.removeButton2}
            onClick={deleteTPU}
          />
        )}
      </div>
      <div className={styles.timeplanUnitTimesContainer2}>
        <TimeInput
          disabled={disabled}
          input={{
            name: 'timeInputFrom',
            value: from,
            onChange: setFrom,
            onBlur: setFrom,
          }}
          className={classNames(styles.timeInput, styles.timeplanUnitTime)}
          formControl={false}
          bigText
          type="time"
        />
        <TimeInput
          disabled={disabled}
          input={{
            name: 'timeInputTo',
            value: to,
            onChange: setTo,
            onBlur: setTo,
          }}
          className={classNames(styles.timeInput, styles.timeplanUnitTime)}
          formControl={false}
          bigText
          type="time"
        />
      </div>
      {hasOvertimePrices && (
        <div className={styles.timeplanUnitTimesContainer2}>
          <label htmlFor="timeInputOvertimeValue" className={styles.overtimeLabel}>Övertid</label>
          <TimeInput
            id="timeInputOvertimeValue"
            disabled={disabled}
            input={{
              name: 'timeInputOvertimeValue',
              value: overtimeValue,
              onChange: setOvertimeMinutes,
              onBlur: setOvertimeMinutes,
            }}
            className={classNames(styles.timeInput, styles.timeplanUnitTime)}
            formControl={false}
            bigText
            type="time"
          />
          h
        </div>
      )}
      <Warnings
        showWarnings={showWarnings}
        duplicates={duplicates}
        overlaps={overlaps}
        timeplanUnit={timeplanUnit}
      />
      {!disabled && absentReasons.size > 0 && (
        <div className={styles.workTypeContainer}>
          <DropdownSelect
            className={styles.absentReasonSelect}
            input={{
              value: absentReason,
              onChange: setAbsentReason,
            }}
            options={absentReasons}
          />
        </div>
      )}
    </div>
  );
}

TimeplanUnit.defaultProps = {
  showWarnings: true,
  overlaps: false,
  duplicates: false,
  isDeletable: true,
  hasOvertimePrices: false,
};

TimeplanUnit.propTypes = {
  disabled: PropTypes.bool.isRequired,
  hasPayment: PropTypes.bool.isRequired,
  timeplanUnit: PropTypes.instanceOf(TimeplanUnitRecord).isRequired,
  overlaps: PropTypes.bool,
  duplicates: PropTypes.bool,
  workTypes: PropTypes.instanceOf(List).isRequired,
  absentReasons: PropTypes.instanceOf(List).isRequired,
  showWarnings: PropTypes.bool,
  isDeletable: PropTypes.bool,
  setFrom: PropTypes.func.isRequired,
  setTo: PropTypes.func.isRequired,
  setWorkType: PropTypes.func.isRequired,
  setAbsentReason: PropTypes.func.isRequired,
  setOvertimeMinutes: PropTypes.func.isRequired,
  hasOvertimePrices: PropTypes.bool,
};

export default compose(
  withTranslation(),
  withProps(({ timeplanUnit }) => {
    const {
      workType,
      from,
      to,
      absentReason,
      payment,
      booking,
      overtimeMinutes,
      hasOvertimePrices,
    } = timeplanUnit.toJS();
    const overtimeValue = moment()
      .startOf('day')
      .add(overtimeMinutes, 'minutes')
      .format('HH:mm');

    return {
      workType,
      from,
      to,
      absentReason,
      hasPayment: !!payment,
      bookingId: booking,
      overtimeValue,
      hasOvertimePrices,
    };
  }),
  branch(({ bookingId }) => !!bookingId,
    compose(
      withBooking,
      mapProps(({ booking, ...props }) => ({
        ...props,
        agreementId: booking.get('customerAgreement'),
      })),
      withAgreement,
      mapProps(({ agreement, ...props }) => ({
        ...props,
        workTypesIds: agreement.get('workTypes'),
      })),
      withWorkTypes,
    ),
  ),
  withProps(({ timeplanUnit, onChangeTimeplanUnits, t }) => ({
    setFrom: from => onChangeTimeplanUnits(setTimeplanUnit, timeplanUnit, { from }),
    setTo: to => onChangeTimeplanUnits(setTimeplanUnit, timeplanUnit, { to }),
    setOvertimeMinutes: (overtimeValue) => {
      const mmtMidnight = moment().startOf('day');
      const overtimeMoment = moment(`${moment().format('YYYY-MM-DD')} ${overtimeValue}`);
      const diffMinutes = overtimeMoment.diff(mmtMidnight, 'minutes');
      onChangeTimeplanUnits(setTimeplanUnit, timeplanUnit, { overtimeMinutes: diffMinutes });
    },
    setWorkType: workType => onChangeTimeplanUnits(setTimeplanUnit, timeplanUnit, { workType }),
    setAbsentReason: absentReason =>
      onChangeTimeplanUnits(setTimeplanUnit, timeplanUnit, { absentReason }),
    deleteTPU: () => {
      Confirmation(
        undefined,
        {
          title: t('Är du säkert på att du vill ta bort passet?'),
          okLabel: t('Ja'),
          cancelLabel: t('Nej'),
        },
      ).then(() => {
        onChangeTimeplanUnits(deleteTimeplanUnit, timeplanUnit);
      }).catch(() => { });
    },
  })),
)(TimeplanUnit);
